import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, ChatBubble, ChatColumn } from '@gitbook/spine-react';

import { AgentMessageActions, getMessageContentText } from './AgentMessageActions';
import { AttachmentsSummary } from './AttachmentsSummary';
import { MessageDocument } from './MessageDocument';

/**
 * Messages the agent has accepted but not answered yet, held for the current response to finish.
 * Rendered like sent user messages, dimmed the way the outbox dims them, so a message keeps the
 * same look from the click through the queue until the agent picks it up. Each entry carries its
 * own controls: run it immediately, or take it back into the composer.
 */
export function AgentChatQueue(props: {
    queue: api.AgentQueuedMessage[];
    onSendNow: (entry: api.AgentQueuedMessage) => void;
    onReturnToComposer: (entry: api.AgentQueuedMessage) => void;
}) {
    const { queue, onSendNow, onReturnToComposer } = props;

    if (queue.length === 0) {
        return null;
    }

    return (
        <ChatColumn className="gap-3 pb-4">
            {queue.map((entry) => (
                <AgentChatQueuedMessage
                    key={entry.id}
                    entry={entry}
                    onSendNow={onSendNow}
                    onReturnToComposer={onReturnToComposer}
                />
            ))}
        </ChatColumn>
    );
}

function AgentChatQueuedMessage(props: {
    entry: api.AgentQueuedMessage;
    onSendNow: (entry: api.AgentQueuedMessage) => void;
    onReturnToComposer: (entry: api.AgentQueuedMessage) => void;
}) {
    const { entry, onSendNow, onReturnToComposer } = props;
    const attachments = entry.message.attachments ?? [];

    return (
        // Deliberately no entrance: the entry takes over from its outbox bubble in place, and the
        // rise already played when the message was sent. Its disappearance is a handoff too — to
        // the real message the turn appends, or to the composer the user pulled it back into.
        <ChatBubble
            dimmed
            footer={
                // No status label: the dimmed bubble already reads as pending, the same way the
                // outbox dims an unacknowledged send. The controls say the rest on hover.
                <AgentMessageActions
                    getText={() => getMessageContentText(entry.message.content)}
                    date={entry.queuedAt}
                >
                    <Button
                        kind="blank"
                        size="xsmall"
                        iconOnly
                        leadingIcon={Icon.X}
                        label="Put back in the composer"
                        onPress={() => onReturnToComposer(entry)}
                    />
                    <Button
                        kind="blank"
                        size="xsmall"
                        iconOnly
                        leadingIcon={Icon.ArrowUp}
                        label="Interrupt the agent and send now"
                        onPress={() => onSendNow(entry)}
                    />
                </AgentMessageActions>
            }
        >
            <MessageDocument content={entry.message.content} />
            {attachments.length > 0 ? (
                <AttachmentsSummary attachments={attachments} size="large" />
            ) : null}
        </ChatBubble>
    );
}
